import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

// --- Types (mirror backend src/lib/meetings/types.ts and the meetings
// route / meetingResearch.service public shapes) --------------------------

export type MeetingResearchStatusApi = 'none' | 'pending' | 'in_progress' | 'done' | 'failed';

export interface MediaAppearance {
  type: string;
  title: string;
  url?: string;
  note?: string;
}

export interface OnlinePresence {
  platform: string;
  url?: string;
  note?: string;
}

export interface MeetingRecommendations {
  briefing: string[];
  talkingPoints: string[];
  commonGround: string[];
  cautions: string[];
}

export interface MeetingResearchReport {
  profile: string;
  onlinePresence: OnlinePresence[];
  mediaAppearances: MediaAppearance[];
  recommendations: MeetingRecommendations;
  tldr: string[];
  sources: string[];
  unverified: boolean;
}

/** One upcoming "Meet [Nume]" meeting with its research status. */
export interface UpcomingMeeting {
  eventId: string;
  calendarEventId: string | null;
  personName: string;
  meetingTitle: string;
  meetingDescription: string;
  meetingDate: string;
  startTime: string;
  endTime: string | null;
  researchStatus: MeetingResearchStatusApi;
  researchId: string | null;
  completedAt: string | null;
}

export interface UpcomingMeetingsResponse {
  meetings: UpcomingMeeting[];
  total: number;
  webSearchEnabled: boolean;
  webSearchProvider: string | null;
}

/** Full research record (mirrors PublicResearch on the backend). */
export interface MeetingResearch {
  id: string;
  eventId: string;
  personName: string;
  meetingDescription: string;
  meetingDate: string;
  status: MeetingResearchStatusApi;
  report: MeetingResearchReport | null;
  sources: string[];
  kbPath: string | null;
  error: string | null;
  startedAt: string | null;
  completedAt: string | null;
  emailSentAt: string | null;
}

export interface ResearchStatus {
  status: MeetingResearchStatusApi;
  completedAt: string | null;
  error: string | null;
}

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** List upcoming "Meet [Nume]" meetings + their research status (CLE-234). */
export async function getUpcomingMeetings(params: {
  userId?: string;
  date?: string;
  days?: number;
}): Promise<UpcomingMeetingsResponse> {
  const qs = new URLSearchParams();
  if (params.userId) qs.set('userId', params.userId);
  if (params.date) qs.set('date', params.date);
  if (params.days) qs.set('days', String(params.days));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const res = await apiFetch<Envelope<UpcomingMeetingsResponse>>(
    `/api/meetings/upcoming${suffix}`,
    { headers: authHeaders() },
  );
  return res.data;
}

/** Trigger (or re-run) research for an event. Returns immediately (async job). */
export async function triggerMeetingResearch(
  eventId: string,
  body?: { personName?: string; meetingDescription?: string; meetingDate?: string },
): Promise<{ researchId: string; status: string }> {
  const res = await apiFetch<Envelope<{ researchId: string; status: string }>>(
    `/api/meetings/${encodeURIComponent(eventId)}/research`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body ?? {}),
    },
  );
  return res.data;
}

/** Read the saved research for an event (status 'none' when not started). */
export async function getMeetingResearch(
  eventId: string,
): Promise<{ status: MeetingResearchStatusApi; research: MeetingResearch | null }> {
  const res = await apiFetch<
    Envelope<{ status: MeetingResearchStatusApi; research: MeetingResearch | null }>
  >(`/api/meetings/${encodeURIComponent(eventId)}/research`, { headers: authHeaders() });
  return res.data;
}

/** Lightweight status poll for an in-progress research job. */
export async function getMeetingResearchStatus(eventId: string): Promise<ResearchStatus> {
  const res = await apiFetch<Envelope<ResearchStatus>>(
    `/api/meetings/${encodeURIComponent(eventId)}/research/status`,
    { headers: authHeaders() },
  );
  return res.data;
}
